import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { IActivityLog, IEmaSettingsChangeRecord, ILocationChangeRecord, IPersonnelChangeRecord, LocationChangeRecordField } from 'app/models/activity-log.model';
import { IdentityFormatPipe, IncludesPipe, MomentPipe, SomePipe } from 'app/pipes/pipes';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from 'app/services/api.service';
import { LocationNavigationService } from 'app/services/location-navigation.service';

@Component({
    selector: 'app-activity-location-details',
    imports: [CommonModule, TranslateModule, RouterModule, IdentityFormatPipe, IncludesPipe, MomentPipe
    ],
    templateUrl: './activity-location-details.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['../activity-details-dialog/activity-details-content-styles.css']
})
export class ActivityLocationDetailsComponent implements OnChanges {
  @Output() closeDialog = new EventEmitter<void>();
  @Input() log: IActivityLog;
  @Input() locations: Location[] = [];

  locationChanges: ILocationChangeRecord;
  personnelChanges: IPersonnelChangeRecord;
  visibleFields: LocationChangeRecordField[] = [];
  visiblePersonnelFields: string[] = [];
  emaChanges: IEmaSettingsChangeRecord;
  visibleEmaFields: string[] = [];
  personnelNames: Record<string, string> = {};

  constructor(private translate: TranslateService, private apiService: ApiService, private navService: LocationNavigationService, private router: Router) {}

  ngOnChanges(): void {
    this.locationChanges = this.log?.details?.locationChanges ?? null;
    this.visibleFields = this.getVisibleFields(this.locationChanges).visibleFields;

    this.personnelChanges = this.log?.details?.locationPersonnelChanges ?? null;
    this.visiblePersonnelFields = [];
    if (this.personnelChanges) {
      if (this.personnelChanges.addedPersonnels?.length) this.visiblePersonnelFields.push('addedPersonnels');
      if (this.personnelChanges.removedPersonnels?.length) this.visiblePersonnelFields.push('removedPersonnels');
    }

    this.emaChanges = this.log?.details?.emaSettingsChanges ?? null;
    if (this.emaChanges?.oldSettings?.priorities && this.emaChanges?.newSettings?.priorities) {
      this.emaChanges.alertPriorities = {
        oldValue: this.emaChanges.oldSettings.priorities,
        newValue: this.emaChanges.newSettings.priorities
      };
    }
    if (this.personnelChanges?.addedPersonnels?.length) {
        this.personnelChanges.addedPersonnels.forEach(id => this.fetchPersonnelName(id));
      }
      
      if (this.personnelChanges?.removedPersonnels?.length) {
        this.personnelChanges.removedPersonnels.forEach(id => this.fetchPersonnelName(id));
      }
    this.visibleEmaFields = this.getVisibleEmaFields(this.emaChanges);
  }

  private fetchPersonnelName(id: string) {
    this.apiService.getLocationStaffList(id).subscribe((res: any) => {
      const person = res.availablePersonnels.find((p: any) => p.id === id);
      if (person) {
        this.personnelNames[id] = `${person.fullName} (${id})`;
      } else {
        this.personnelNames[id] = id;
      }
    });
  }
  
  private getVisibleFields(locationChanges: ILocationChangeRecord): { visibleFields: LocationChangeRecordField[] } {
    if (!locationChanges) {
      return { visibleFields: [] };
    }
    const fields: LocationChangeRecordField[] = [];

    (['name', 'qualifier', 'address', 'postCode', 'postOffice', 'mainUserGsmNumber', 'contactPhone', 'emaTagOnly', 'networkFailureState'] as const)
      .forEach((key) => {
        const change = locationChanges[key];
        if (!change) return;

        const oldVal = change.oldValue;
        const newVal = change.newValue;

        if (oldVal !== newVal) {
          fields.push(key);
        }
      });
    return { visibleFields: fields };
  }

  getEmaValue(field: string, settings: any): any {
    const val = settings?.[field];
    if (val == null) return null;
    return typeof val === 'object' && 'value' in val ? val.value : val;
  }

private getVisibleEmaFields(emaChanges: any): string[] {
    if (!emaChanges?.hasChanges) return [];

    const fields: string[] = [];
    const oldSettings = emaChanges.oldSettings || {};
    const newSettings = emaChanges.newSettings || {};

    Object.keys(newSettings).forEach(key => {
        if (key === 'priorities') return;

        const oldVal = oldSettings[key];
        const newVal = newSettings[key];

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            fields.push(key);
        }
    });

    if (oldSettings.priorities && newSettings.priorities) {
        emaChanges.alertPriorities = {
            oldValue: oldSettings.priorities,
            newValue: newSettings.priorities
        };
        fields.push('alertPriorities');
    }
    return fields;
}

  getMinuteValue(field: string, settings: any): number | null {
    if (!settings) return null;
    const raw = settings[field];
  
    if (raw == null) return null;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'object' && 'value' in raw) return raw.value;
  
    return null;
  }

  getAlertPrioritiesDisplay(): {
    priority: number;
    oldSound: string;
    newSound: string;
    oldColour: string;
    newColour: string;
    movedTypes: { type: string; from: number; to: number }[];
  }[] {
    if (!this.emaChanges?.alertPriorities) return [];
  
    const oldPriorities = this.emaChanges.alertPriorities.oldValue || [];
    const newPriorities = this.emaChanges.alertPriorities.newValue || [];
  
    const result = [];
  
    for (let i = 0; i < newPriorities.length; i++) {
      const oldP = oldPriorities[i] || { sound: '-', colour: '-', fixedAlertTypes: [], configAlertTypes: [] };
      const newP = newPriorities[i];
  
      const movedTypes = this.getMovedAlertTypes(oldP, newP, oldPriorities, newP.priority);
  
      if (oldP.sound !== newP.sound || oldP.colour !== newP.colour || movedTypes.length > 0) {
        result.push({
          priority: newP.priority,
          oldSound: oldP.sound || '-',
          newSound: newP.sound || '-',
          oldColour: oldP.colour || '-',
          newColour: newP.colour || '-',
          movedTypes
        });
      }
    }
    return result;
  }

  private getMovedAlertTypes(oldP: any, newP: any, allOldPriorities: any[], newPriorityNumber: number): { type: string; from: number; to: number }[] {
    const oldTypesMap: Record<string, number> = {};
    allOldPriorities.forEach((p, index) => {
      const types = [...(p.fixedAlertTypes || []), ...(p.configAlertTypes || [])];
      types.forEach(t => {
        oldTypesMap[t] = p.priority;
      });
    });
  
    const newTypes = [...(newP.fixedAlertTypes || []), ...(newP.configAlertTypes || [])];
  
    const moved: { type: string; from: number; to: number }[] = [];
  
    newTypes.forEach(t => {
      const fromPriority = oldTypesMap[t];
      if (fromPriority !== undefined && fromPriority !== newPriorityNumber) {
        moved.push({ type: t, from: fromPriority, to: newPriorityNumber });
      }
    });
  
    return moved;
  }

  getPriorityMoveGroups(): { fromPriority: number; toPriority: number; types: string[] }[] {
    const oldPriorities = this.emaChanges?.alertPriorities?.oldValue || [];
    const newPriorities = this.emaChanges?.alertPriorities?.newValue || [];
  
    const oldMap: Record<string, number> = {};
    oldPriorities.forEach(p => {
      const types = [...(p.fixedAlertTypes || []), ...(p.configAlertTypes || [])];
      types.forEach(t => (oldMap[t] = p.priority));
    });
  
    const moves: { fromPriority: number; toPriority: number; types: string[] }[] = [];
  
    newPriorities.forEach(newP => {
      const newTypes = [...(newP.fixedAlertTypes || []), ...(newP.configAlertTypes || [])];
  
      const fromGroups: Record<number, string[]> = {};
  
      newTypes.forEach(t => {
        const oldPri = oldMap[t];
        if (oldPri !== undefined && oldPri !== newP.priority) {
          if (!fromGroups[oldPri]) fromGroups[oldPri] = [];
          fromGroups[oldPri].push(t);
        }
      });
  
      Object.keys(fromGroups).forEach(from => {
        moves.push({
          fromPriority: Number(from),
          toPriority: newP.priority,
          types: fromGroups[Number(from)]
        });
      });
    });
  
    return moves;
  }

  getAddedNotes(): string[] {
    const oldNotes = this.emaChanges?.oldSettings?.notes || [];
    const newNotes = this.emaChanges?.newSettings?.notes || [];
    return newNotes.filter(n => !oldNotes.includes(n));
  }
  
  getRemovedNotes(): string[] {
    const oldNotes = this.emaChanges?.oldSettings?.notes || [];
    const newNotes = this.emaChanges?.newSettings?.notes || [];
    return oldNotes.filter(n => !newNotes.includes(n));
  }

  getHiddenFieldCount(): number {
    const totalShown = 15;
    const totalFields = this.visibleFields.length;
    return totalFields > totalShown ? totalFields - totalShown : 0;
  }

  getEmaTranslation(field: string, option?: any): string {
    const fieldMap: Record<string,string> = {
      delivery: 'DELIVERY_TIMEOUT',
      nurseTask: 'NURSE_TASK_TIMEOUT',
      value: 'LOGIN_KEYS'
    };
  
    const optionMap: Record<string, Record<string,string>> = {
      offlineDelay: {
        OFFLINE_IMMEDIATE_ALARM: 'OFFLINE_IMMEDIATE_ALARM',
        OFFLINE_NO_ALARM: 'OFFLINE_NO_ALARM'
      },
      loginKeys: {
        LOGIN_KEYS_DISABLED: 'LOGIN_KEYS_DISABLED',
        ENABLED_WITHOUT_PIN: 'ENABLED_WITHOUT_PIN',
        ENABLED_WITH_PIN_4_PLUS: 'ENABLED_WITH_PIN_4_PLUS'
      }
    };

    if (option !== undefined) {
        if (optionMap[field] && optionMap[field][option]) {
          return this.translate.instant(optionMap[field][option]);
        }
        return this.translate.instant(option.toString());
      }
  
    return this.translate.instant(fieldMap[field] ?? field.toUpperCase());
  }

  isNumeric(val: any): boolean {
    return !isNaN(parseFloat(val)) && isFinite(val);
  }
   
  getTranslatedTypes(types: string[]): string {
    return types.map(t => this.translate.instant(t)).join(', ');
  }

    openLocation(locationId: string, event?: Event) {
    event?.preventDefault();
    this.closeDialog.emit();
    this.navService.openLocation(locationId);
    this.router.navigate(['/device']);
  }
}